import {
  applyFormat$,
  applyListType$,
  ButtonWithTooltip,
  currentFormat$,
  currentListType$,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  MDXEditor,
  type MDXEditorMethods,
  MultipleChoiceToggleGroup,
  markdownShortcutPlugin,
  Separator,
  SingleChoiceToggleGroup,
  toolbarPlugin,
  useCellValues,
  usePublisher,
} from "@mdxeditor/editor";
import { forwardRef, type RefObject, useEffect, useRef } from "react";
import i18n from "senf-shared/utils/i18n";
import "@mdxeditor/editor/style.css";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useMeasure } from "../../../hooks/useMeasure";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../icon/Icon";
import InsertLinkPopover from "./InsertLinkPopover";
import { LinkButton } from "./LinkButton";
import { preventUnderlinePlugin } from "./preventUnderlinePlugin";
import {
  TextEnhancementControls,
  TextEnhancementProvider,
  TextEnhancementWrapper,
  useTextEnhancementContext,
} from "./textEnhancement";

const EditorContainer = styled.div`
  font-size: 16px;
  position: relative;
  padding: 0.5rem;
  margin: -0.5rem;
  .mdxeditor-toolbar {
    color: var(--text-default);
    position: absolute;
    pointer-events: none;
    opacity: 0;
    top: -42px;
    right: 0px;
    padding: 0 4px;
    background-color: var(--background-default);
    border-radius: var(--rounded-md) var(--rounded-md) 0 0;
    border: 1px solid var(--border-subdued);
    transition: opacity 0.15s ease;
    z-index: 10;
    [data-orientation="horizontal"] {
      display: flex;
      flex-direction: row;
    }
    & > * {
      margin-inline: 0;
      button {
        padding: 2px;
        border-radius: var(--rounded-sm) !important;
        width: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &:hover {
          background-color: var(--surface-hovered);
          color: var(--text-highlight);
        }
        &:active {
          svg {
            transform: scale(0.95);
          }
        }
        &:active,
        &[data-state="on"] {
          background-color: var(--action-primary-default);
          color: var(--text-on-primary);
        }
      }
    }
  }
  &:focus-within {
    [data-placeholder] {
      color: var(--text-placeholder);
    }
  }
  [data-placeholder] {
    color: var(--text-disabled);
  }

  [contenteditable],
  .mdxeditor-root-contenteditable > * {
    /* position: absolute; */
    /* left: 0; */
    /* padding: 0; */
    & > * + * {
      margin-top: 1em;
    }
  }
  [list-item] {
    margin: 0;
  }

  /* Inline code styling */
  code {
    font-family: inherit;
    font-size: inherit;
    background-color: var(--surface-subdued);
    border-radius: var(--rounded-sm);
    padding: 0.125rem 0.25rem;
    border: 1px solid var(--border-subdued);
  }

  /* Underlined additions (green code style) */
  u {
    text-decoration: none;
    font-family: inherit;
    font-size: inherit;
    background-color: var(--surface-success-subdued, #dcfce7);
    border-radius: var(--rounded-sm);
    padding: 0.125rem 0.25rem;
    border: 1px solid var(--border-success-subdued, #86efac);
  }

  /* Strikethrough deletions */
  s {
    text-decoration: line-through;
    font-family: inherit;
    font-size: inherit;
  }
`;
const IS_BOLD = 0b1 as const;
const IS_ITALIC = 0b10 as const;

const LIST_TITLE_MAP = {
  bullet: i18n.t("global:bulletedList"),
  number: i18n.t("global:numberedList"),
  bold: "Bold",
  italic: "Italic",
};
const ICON_NAME_MAP = {
  bold: "bold",
  italic: "italic",
  bullet: "list",
  number: "numbered-list",
};
const toggle = (type) => ({
  value: type,
  title: LIST_TITLE_MAP[type] as string,
  contents: <Icon name={ICON_NAME_MAP[type]} className="w-5" />,
});
const BoldItalicToggles = ({ options = ["bold", "italic"] }) => {
  const [currentFormat] = useCellValues(currentFormat$);
  const applyFormat = usePublisher(applyFormat$);

  const ACTIVE_MAP = {
    bold: (currentFormat & IS_BOLD) !== 0,
    italic: (currentFormat & IS_ITALIC) !== 0,
  };

  return (
    <MultipleChoiceToggleGroup
      items={options.map((type) => {
        const item = toggle(type);
        return {
          ...item,
          active: ACTIVE_MAP[type],
          onChange: () => applyFormat(type === "bold" ? "bold" : "italic"),
        };
      })}
    />
  );
};
const ListsToggle = ({ options = ["bullet", "number"] }) => {
  const [currentListType] = useCellValues(currentListType$);
  const applyListType = usePublisher(applyListType$);
  return (
    <SingleChoiceToggleGroup
      value={currentListType || ""}
      items={options.map(toggle)}
      onChange={applyListType}
    />
  );
};

const CustomButton = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    // <button
    //   onClick={onClick}
    //   type="button"
    //   className="group/magic p-1 hover:bg-surface-hovered rounded-sm -mr-1"
    // >
    //   <Icon
    //     name="ai"
    //     className="w-5 group-hover/magic:text-text-highlight"
    //   />
    // </button>
    <ButtonWithTooltip
      title={t("util:enhance", { val: t("global:text") })}
      onClick={onClick}
      className="hover:bg-surface-hovered w-6 h-6 flex items-center justify-center cursor-pointer rounded-sm"
    >
      <Icon
        name="colorful/ai"
        className="w-5 group-hover/magic:text-text-highlight"
      />
    </ButtonWithTooltip>
  );
};

const CustomToolbar = ({ magicButtonClick }) => {
  return (
    <>
      <CustomButton onClick={magicButtonClick} />
      <Separator />
      <BoldItalicToggles options={["bold", "italic"]} />
      <Separator />
      <ListsToggle />
      <Separator />
      <LinkButton />
    </>
  );
};

interface RichTextEditorProps {
  value: string;
  isResetting?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  propSetIsFocused?: (isFocused: boolean) => void;
  size?: string;
  textAlign?: string;
  className?: string;
  contentEditableClassName?: string;
  /** Override UI language for AI text enhancement responses (ISO code, e.g. from a translation column) */
  aiResponseLang?: string;
}

const getCurrentBlockTextBeforeCursor = (): string => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return "";

  const range = selection.getRangeAt(0).cloneRange();
  range.collapse(true);

  const startNode =
    range.startContainer.nodeType === Node.ELEMENT_NODE
      ? (range.startContainer as Element)
      : range.startContainer.parentElement;

  const block = startNode?.closest("p, li");
  if (!block) return "";

  const prefixRange = range.cloneRange();
  prefixRange.selectNodeContents(block);
  prefixRange.setEnd(range.startContainer, range.startOffset);
  return prefixRange.toString();
};

const preserveOrderedListStart = (markdown: string, start: number) => {
  const numberedMarkerRegex = /^1\.(\s|$)/m;
  if (start <= 1) return markdown;
  if (
    new RegExp(`^${start}\\)\\s`, "m").test(markdown) ||
    new RegExp(`<ol[^>]*\\sstart="${start}"[^>]*>`, "i").test(markdown)
  ) {
    return markdown;
  }

  if (numberedMarkerRegex.test(markdown)) {
    const matches = [...markdown.matchAll(/^1\.(\s|$)/gm)];
    const last = matches[matches.length - 1];
    if (last?.index !== undefined) {
      const trailing = last[1] ?? "";
      return (
        markdown.slice(0, last.index) +
        `${start}.${trailing}` +
        markdown.slice(last.index + 2 + trailing.length)
      );
    }
  }

  if (/^1\)\s/m.test(markdown)) {
    const matches = [...markdown.matchAll(/^1\)\s/gm)];
    const last = matches[matches.length - 1];
    if (last?.index !== undefined) {
      return (
        markdown.slice(0, last.index) + `${start}) ` + markdown.slice(last.index + 3)
      );
    }
  }

  if (/^1\\\.\s/m.test(markdown)) {
    const matches = [...markdown.matchAll(/^1\\\.\s/gm)];
    const last = matches[matches.length - 1];
    if (last?.index !== undefined) {
      return (
        markdown.slice(0, last.index) +
        `${start}\\. ` +
        markdown.slice(last.index + 4)
      );
    }
  }

  if (/<ol(?:\s+[^>]*)?>/i.test(markdown)) {
    return markdown.replace(/<ol((?:\s+[^>]*)?)>/i, (_match, attrs: string) => {
      if (/\sstart=/.test(attrs)) return `<ol${attrs}>`;
      return `<ol${attrs} start="${start}">`;
    });
  }

  if (new RegExp(`${start}\\\\\\.&#x20;`).test(markdown)) {
    return markdown.replace(new RegExp(`${start}\\\\\\.&#x20;`, "g"), `${start}. `);
  }

  // Fix the start number of the last contiguous ordered-list block.
  // This avoids false positives when another list item (e.g. "2.") exists in the same block.
  const lines = markdown.split("\n");
  let blockEnd = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (/^\d+\.(\s|$)/.test(lines[i])) {
      blockEnd = i;
      break;
    }
  }
  if (blockEnd !== -1) {
    let blockStart = blockEnd;
    while (blockStart - 1 >= 0 && /^\d+\.(\s|$)/.test(lines[blockStart - 1])) {
      blockStart -= 1;
    }
    const firstLine = lines[blockStart];
    const firstMatch = /^(\d+)\.(\s|$)/.exec(firstLine);
    if (firstMatch) {
      const currentStart = Number(firstMatch[1]);
      if (currentStart === start) return markdown;
      lines[blockStart] = firstLine.replace(/^\d+\./, `${start}.`);
      return lines.join("\n");
    }
  }

  return markdown;
};

const Editor = ({
  value,
  isResetting,
  placeholder,
  onChange,
  mdxEditorRef,
  containerRef,
  size,
  textAlign,
  contentEditableClassName,
  setPendingOrderedListStart,
  ...props
}) => {
  const { setIsOpen } = useTextEnhancementContext();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === " ") {
      const currentLine = getCurrentBlockTextBeforeCursor().trim();
      const match = /^(\d+)\.$/.exec(currentLine);
      if (match) {
        setPendingOrderedListStart(Number(match[1]));
      }
    }

    if (event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();

      // Shift+Tab goes backward, Tab goes forward
      const focusableElements = Array.from(
        document.querySelectorAll<HTMLElement>(
          ':is(a, button, input, textarea, select, [contenteditable], [tabindex]):not([tabindex="-1"])',
        ),
      ).filter(
        (el) =>
          !el.hasAttribute("disabled") &&
          !el.getAttribute("aria-hidden") &&
          !el.closest?.(".mdxeditor-toolbar"),
      );

      const index = focusableElements.indexOf(
        document.activeElement as HTMLElement,
      );
      let next = event.shiftKey ? index - 1 : index + 1;
      let nextElement = focusableElements[next];

      // When leaving the editor with Shift+Tab, skip the wrapper (it would re-focus the editor)
      if (
        event.shiftKey &&
        nextElement === containerRef?.current &&
        next - 1 >= 0
      ) {
        next = next - 1;
        nextElement = focusableElements[next];
      }

      if (nextElement) {
        nextElement.focus();
      }
    }
  };

  return (
    <div className="relative [&:focus-within_.mdxeditor-toolbar]:pointer-events-auto [&:focus-within_.mdxeditor-toolbar]:opacity-100">
      <EditorContainer onKeyDownCapture={handleKeyDown}>
        <MDXEditor
          ref={mdxEditorRef}
          key={isResetting ? "reset" : undefined}
          markdown={value || ""}
          placeholder={placeholder}
          className={cn(
            "markdown relative whitespace-pre-wrap",
            "[&_[contenteditable]+div]:whitespace-normal",
            "[&_[contenteditable]+div]:p-0",
            props?.textAlign === "center" && "centeredMarkdown",
            "font-sans text-current",
            size,
          )}
          contentEditableClassName={contentEditableClassName}
          onChange={onChange}
          plugins={[
            linkPlugin(),
            linkDialogPlugin({
              LinkDialog: () => <InsertLinkPopover />,
            }),
            listsPlugin(),
            markdownShortcutPlugin(),
            preventUnderlinePlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <CustomToolbar magicButtonClick={() => setIsOpen(true)} />
              ),
            }),
          ]}
        />
        <TextEnhancementControls />
      </EditorContainer>
    </div>
  );
};

const RichTextEditor = forwardRef<MDXEditorMethods, RichTextEditorProps>(
  (
    {
      value,
      isResetting,
      placeholder,
      onChange,
      propSetIsFocused = () => {},
      size,
      className,
      contentEditableClassName,
      aiResponseLang,
      ...props
    },
    ref,
  ) => {
    const fallbackRef = useRef<MDXEditorMethods>(null);
    const mdxEditorRef = ref || fallbackRef;
    const [{ ref: containerRef }, dimensions] = useMeasure<HTMLInputElement>({
      trackPosition: false,
    });

    useEffect(() => {
      const editor = (mdxEditorRef as unknown as RefObject<MDXEditorMethods>)
        ?.current;
      if (editor) {
        const current = editor.getMarkdown() || "";
        const incoming = value || "";
        const isEditorFocused =
          !!containerRef.current &&
          !!document.activeElement &&
          containerRef.current.contains(document.activeElement);

        if (current === incoming) return;
        if (isEditorFocused) return;
        editor.setMarkdown(incoming);
      }
    }, [value, mdxEditorRef, containerRef]);

    return (
      <TextEnhancementProvider
        mdxEditorRef={mdxEditorRef as RefObject<MDXEditorMethods | null>}
        onChange={onChange}
        value={value}
        aiResponseLang={aiResponseLang}
      >
        <RichTextEditorContent
          value={value}
          isResetting={isResetting}
          placeholder={placeholder}
          onChange={onChange}
          propSetIsFocused={propSetIsFocused}
          size={size}
          className={className}
          contentEditableClassName={contentEditableClassName}
          mdxEditorRef={mdxEditorRef as RefObject<MDXEditorMethods | null>}
          containerRef={containerRef as RefObject<HTMLInputElement | null>}
          dimensions={dimensions}
          {...props}
        />
      </TextEnhancementProvider>
    );
  },
);

const RichTextEditorContent = ({
  value,
  isResetting,
  placeholder,
  onChange,
  propSetIsFocused,
  size,
  className,
  contentEditableClassName,
  mdxEditorRef,
  containerRef,
  dimensions,
  ...props
}: RichTextEditorProps & {
  mdxEditorRef: RefObject<MDXEditorMethods | null>;
  containerRef: RefObject<HTMLInputElement | null>;
  dimensions: { width: number; left: number; height: number };
}) => {
  const { isOpen, initialValue, generatedValue } = useTextEnhancementContext();
  const pendingOrderedListStartRef = useRef<{
    start: number;
  } | null>(null);

  // Adjust margin-bottom when text enhancement box might overflow
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const adjustMarginBottom = () => {
      const container = containerRef.current;
      const textEnhanceBox = document.getElementById("textEnhanceBox");

      if (!container || !textEnhanceBox) return;

      const containerRect = container.getBoundingClientRect();
      const textEnhanceBoxRect = textEnhanceBox.getBoundingClientRect();

      // Calculate distance from container bottom to document bottom
      const documentHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        window.innerHeight,
      );
      const containerBottom = containerRect.bottom + window.scrollY;
      const distanceToDocumentBottom = documentHeight - containerBottom;

      // Required space: textEnhanceBox height + 20px buffer
      const requiredSpace = textEnhanceBoxRect.height;

      if (distanceToDocumentBottom < requiredSpace) {
        // Not enough space, add margin-bottom
        container.style.marginBottom = `${requiredSpace}px`;
      } else {
        // Enough space, remove any added margin-bottom
        container.style.marginBottom = "";
      }
    };

    // Delay to ensure textEnhanceBox is rendered and positioned
    const timeoutId = setTimeout(adjustMarginBottom, 100);

    // Also adjust on window resize
    window.addEventListener("resize", adjustMarginBottom);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", adjustMarginBottom);
      // Reset margin when popover closes
      if (containerRef.current) {
        containerRef.current.style.marginBottom = "";
      }
    };
  }, [isOpen, containerRef]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: div needs focus/blur handlers
    <div
      className={cn("w-full", className)}
      tabIndex={-1}
      onFocus={(e) => {
        propSetIsFocused?.(true);
        // When focus lands on the wrapper (e.g. from Tab), forward to the editor
        requestAnimationFrame(() => {
          if (
            e.target === containerRef.current ||
            e.target.getAttribute?.("role") === "toolbar"
          ) {
            const md = mdxEditorRef?.current;
            if (!md?.getMarkdown()) {
              md?.setMarkdown("&npsb;");
              md?.focus(() => md?.setMarkdown(""), {
                defaultSelection: "rootStart",
              });
            } else {
              md?.focus();
            }
          }
        });
      }}
      onFocusCapture={(e) => {
        // Whenever focus enters the container (wrapper or contenteditable), ensure the editor is activated
        if (containerRef.current?.contains(e.target as Node)) {
          propSetIsFocused?.(true);
          requestAnimationFrame(() => {
            mdxEditorRef?.current?.focus();
          });
        }
      }}
      onBlur={(e) => {
        // Only clear focused state when focus leaves the container (not when moving to editor)
        const next = e.relatedTarget as Node | null;
        if (!containerRef.current?.contains(next)) {
          propSetIsFocused?.(false);
        }
      }}
      ref={containerRef}
      data-text-enhancement-preview={
        isOpen &&
        generatedValue.current.value &&
        mdxEditorRef?.current?.getMarkdown() !== initialValue.current
      }
    >
      <Editor
        value={value}
        onChange={(nextValue) => {
          const pendingState = pendingOrderedListStartRef.current;
          const pendingStart = pendingState?.start ?? null;
          const patchedValue = pendingStart
            ? preserveOrderedListStart(nextValue, pendingStart)
            : nextValue;
          if (pendingStart && pendingState) {
            const stillTypingEscapedMarker = new RegExp(
              `${pendingStart}\\\\\\.&#x20;`,
            ).test(nextValue);
            const correctedThisPass = patchedValue !== nextValue;
            const stillShowsLeadingOne = /^1\.(\s|$)/m.test(nextValue);

            // Keep correcting while the editor keeps normalizing back to 1.
            // Once no correction is needed and no transient marker remains, clear.
            pendingOrderedListStartRef.current =
              correctedThisPass || stillTypingEscapedMarker || stillShowsLeadingOne
                ? { start: pendingStart }
                : null;
          }
          onChange(patchedValue);
        }}
        mdxEditorRef={mdxEditorRef}
        containerRef={containerRef}
        size={size}
        textAlign={props.textAlign}
        isResetting={isResetting}
        placeholder={placeholder}
        contentEditableClassName={contentEditableClassName}
        setPendingOrderedListStart={(start: number) => {
          pendingOrderedListStartRef.current = {
            start,
          };
        }}
      />
      <TextEnhancementWrapper dimensions={dimensions} />
    </div>
  );
};

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
